<template>
  <v-app id="app">
    <side-menu v-if="!$getFirstValue() && checkedToken" :is-menu-open.sync="isMenuOpen" />
    <toolbar v-if="checkedToken" :is-menu-open.sync="isMenuOpen" />

    <v-content>
      <v-container v-if="checkedToken" fluid class="pa-0">
        <transition-group name="slide-up">
          <div v-if="$isLogged()" key="page-template">
            <beamer v-if="!isMobile" />
          </div>
          <div key="page">
            <router-view class="child-view" />
          </div>
        </transition-group>
      </v-container>
      <mf-loading-dialog v-else :loading="!checkedToken" />
    </v-content>
  </v-app>
</template>

<script>
import { mapActions } from 'vuex'
import { mapWaitingActions } from 'vue-wait'
import 'driver.js/dist/driver.css'
import userflow from 'userflow.js'
import LogRocket from 'logrocket'

import Toolbar from '@/components/Toolbar.vue'
import SideMenu from '@/components/SideMenu.vue'
import Beamer from '@/plugins/beamer/Beamer.vue'
import { activeZendesk } from '@/external-libs/zendesk.js'
import { getFingerprint } from '@/services/fingerprint.js'
import { QUERY_REFRESH, QUERY_SINGLE_LOGIN } from '@/graphql'
import { formattedRedirectAfterLogin, formattedRedirectLogout } from '@/helpers/login'
import { QUERY_ACCOUNT_FRANCHISEE_PERMISSIONS } from '@/modules/configuracoes/permissionamento-filiado/graphql'
import { QUERY_ZENDESK_CHAT_TOKEN } from './graphql'

const ZAFFARI_ID_ACCOUNT = '5ca3a137b4e5562125d5a703'
const STOK_ID_ACCOUNT = '5ca3a0e2b4e556204fd86e54'

export default {
  components: {
    SideMenu,
    Toolbar,
    Beamer,
  },
  data: () => ({
    checkedToken: false,
    isMenuOpen: false,
  }),
  computed: {
    isFranchisee() {
      return this.$getLoggedUser().is_franchisee
    },
    onlyMercapromo() {
      return this.$getLoggedUser().only_mercapromo
    },
    franchiseePermissionsRoutes() {
      return this.$store.getters.franchiseePermissionsRoutes
    },
  },
  watch: {
    $route(route) {
      if (route?.query?.auth_code) {
        this.removeAuthCodeFromURL()
      }
    },
  },
  async created() {
    if (this.$isLogged()) {
      await this.verifyRefreshToken()
    } else {
      await this.validateAuthcode()
    }

    const userLogged = this.$getLoggedUser()
    const userEmail = userLogged.username

    activeZendesk({
      authenticate: async () => {
        try {
          const {
            data: {
              zendeskWidgetToken: { token },
            },
          } = await this.$apollo.query({
            query: QUERY_ZENDESK_CHAT_TOKEN,
            fetchPolicy: 'no-cache',
            variables: { userEmail },
          })
          return token
        } catch (error) {
          return null
        }
      },
    })

    userflow.identify(userLogged.id_user, {
      email: userEmail,
      name: userLogged.name,
      client: userLogged.client,
      signed_up_at: new Date().toISOString(),
      profiles: userLogged.profiles?.toString(),
      isAdmin: userLogged.mf,
      isFranchise: userLogged.is_franchise,
      isFranchisee: userLogged.is_franchisee,
    })

    if ([ZAFFARI_ID_ACCOUNT, STOK_ID_ACCOUNT].includes(userLogged.id_account)) {
      LogRocket.init('xrumjv/mystique')

      LogRocket.identify(userLogged.id_user, {
        name: userLogged.name,
        email: userLogged.username,
      })
    }

    try {
      await this.$store.dispatch('getStates', [
        'getLastUpdate',
        'empresaPrimeiroRegistro',
        'fetchUnits',
        'fetchIndustries',
        'fetchUnitsGroups',
        'fetchPlatformNotifications',
      ])
    } catch (error) {
      console.error(error)
    }
  },
  methods: {
    ...mapActions([
      'setUserPermissions',
      'getFranchiseePermissions',
      'setFranchiseePermissions',
      'fetchUnits',
      'fetchIndustries',
      'fetchUnitsGroups',
      'fetchPlatformNotifications',
    ]),
    ...mapWaitingActions({
      empresaPrimeiroRegistro: 'primeiro-registro',
    }),
    async validateAuthcode() {
      this.checkedToken = false

      try {
        const authCode = this.getAuthCodeFromURL()
        let hash = window.localStorage.getItem('redirectAfterLogin')

        if (!authCode) {
          const singleLoginURL = this.$microservicesUrls['mfLogin']
          const host = this.getHost()
          hash = hash || window.location.hash.slice(1) || ''
          window.localStorage.setItem('redirectAfterLogin', hash)
          window.location.href = formattedRedirectAfterLogin(singleLoginURL, host, hash)
        }

        this.$setAuthCode(authCode)

        const {
          data: {
            loginWithAuthCode: { user, ecommerceFlag, useCatalogFlag, cbmFlag },
          },
        } = await this.$apollo.query({
          query: QUERY_SINGLE_LOGIN,
          fetchPolicy: 'no-cache',
          variables: {
            authCode: this.$getAuthCode(),
          },
        })
        await this.setUserData(user, ecommerceFlag, useCatalogFlag, cbmFlag)

        if (hash) this.redirectToSpecificRoute(hash)
        else this.redirectToFirstPermission(user.pages || [])
      } catch (error) {
        console.log(error)
        const singleLoginURL = this.$microservicesUrls['mfLogin']
        const host = location.href.slice(0, location.href.indexOf('#'))
        const hash = window.localStorage.getItem('redirectAfterLogin') || ''

        this.$logout()
        window.location.href = formattedRedirectLogout(singleLoginURL, host, hash)
      } finally {
        this.checkedToken = true
      }
    },
    getAuthCodeFromURL() {
      return window.location.hash.slice(window.location.hash.indexOf('?') + 1).split('=')[1]
    },
    getHost() {
      return location.href.slice(0, location.href.indexOf('#'))
    },
    async getAccountFranchiseePermissions(user) {
      try {
        const { data } = await this.$apollo.query({
          query: QUERY_ACCOUNT_FRANCHISEE_PERMISSIONS,
          fetchPolicy: 'no-cache',
        })

        this.setFranchiseePermissions({
          ...data.accountFranchiseePermissions,
          managing_user_requests: false,
        })

        const blockedRoutes = []
        const availableFranchiseePermissions = Object.keys(this.franchiseePermissionsRoutes)
        for (const permission of availableFranchiseePermissions) {
          const accountHasPermission = data.accountFranchiseePermissions[permission]
          if (!accountHasPermission) {
            const route = this.franchiseePermissionsRoutes[permission]
            blockedRoutes.push(route)
          }
        }

        const userRoutes = user.pages
        const allowedRoutes = []
        for (const route of userRoutes) {
          if (!blockedRoutes.includes(route)) {
            allowedRoutes.push(route)
          }
        }

        this.setUserPermissions(allowedRoutes)
      } catch (error) {
        console.error(error)
      }
    },

    async setUserData(user, ecommerceFlag, useCatalogFlag, cbmFlag) {
      const fingerprint = await getFingerprint()
      this.$setFingerPrint(fingerprint)

      this.setUserPermissions(user.pages || [])
      if (user.multi_user) {
        this.$setIsMultiUser(true)
      }

      this.$setLoggedUser({
        name: user.name,
        username: user.login,
        mf: user.mf,
        admin: user.admin,
        client: user.account.name,
        profiles: user.profiles,
        id_external: user.account.id_external,
        id_account: user.account._id,
        id_user: user._id,
        is_franchise: user.account.is_franchise,
        is_franchisee: user.account.is_franchisee,
        has_mercapromo: user.account.has_mercapromo,
        only_mercapromo: user.account.only_mercapromo,
        tribanco: user.account.tribanco,
        ecommerce_url: user?.account?.channels?.ecommerce_v2?.integration?.url ?? '',
        has_ecommerce: ecommerceFlag ?? false,
        has_club: cbmFlag ?? false,
        ecommercePermissions: user.ecommercePermissions ?? [],
        recencyCriteria: user?.account?.cbm_configuration?.recency_criteria,
        integrationType: user?.account?.integration_type,
        db_name: user.account.db_name,
      })

      this.$setIsAdmin(user.mf)
      this.$setIsEcommerce(ecommerceFlag)
      this.$setIsClub(user.club)
      this.$setUseCatalogFlag(useCatalogFlag)

      this.$setSecurity(user.account.security)
      this.$setAllowedModules(user.account.modules)
      this.$setPiiAccess(user.has_pii_access || false)
      this.$setPiiDownloadAccess(user.has_pii_download_data_permission || false)
      this.$setPiiEmployeesAccess(user.has_pii_employees_access || false)

      if (this.isFranchisee) {
        await this.getAccountFranchiseePermissions(user)
          .then(() => {
            const autoRedirectRoute = window.localStorage.getItem('redirectAfterLogin')
            if (autoRedirectRoute) this.redirectToSpecificRoute(autoRedirectRoute)
            else {
              this.$router.go('/home')
            }
          })
          .catch(() => {
            this.$router.go('/home')
          })
      } else {
        const autoRedirectRoute = window.localStorage.getItem('redirectAfterLogin')
        if (autoRedirectRoute) this.redirectToSpecificRoute(autoRedirectRoute)
        else {
          this.$router.go('/home')
        }
      }
    },

    verifyRefreshToken() {
      this.checkedToken = false
      return this.$apollo
        .query({
          query: QUERY_REFRESH,
          fetchPolicy: 'no-cache',
        })
        .then(() => {
          this.checkedToken = true
        })
        .catch(() => {
          this.checkedToken = true
          this.$router.push('/expired-session')
        })
    },
    redirectToSpecificRoute(route) {
      if (this.$getIsEcommerce()) {
        this.$router.push('/home')
        return
      }

      window.localStorage.removeItem('redirectAfterLogin')
      this.$router.push(route)
    },
    redirectToFirstPermission(permissions) {
      if (this.$getIsEcommerce() || this.onlyMercapromo) {
        this.$router.push('/home')
      }
      const firstPermission = permissions.includes('home') ? 'home' : permissions[0]

      const parent = this.$router.options.routes.find((route) => {
        if (route.meta && route.meta.pagePermission === firstPermission) return route
        else {
          if (route.children) {
            const children = route.children.find((children) => {
              if (children.meta && children.meta.pagePermission === firstPermission) return children
            })
            return children
          }
        }
      })

      if (parent.children) {
        const children = parent.children.find((children) => {
          return children.meta && children.meta.pagePermission === firstPermission
        })
        if (children.path === 'chatbot/alteracao-encarte/novo') {
          this.$router.push('/mobile/chatbot/')
        } else {
          this.$router.push(`${parent.path}/${children.path}`)
        }
      } else {
        this.$router.push(`${parent.path}`).catch(() => {})
      }
    },
    removeAuthCodeFromURL() {
      this.$router.replace({
        ...this.$router.currentRoute,
        query: {
          auth_code: undefined,
        },
      })
    },
  },
}
</script>

<style lang="scss">
.v-application {
  font-family: 'Lato', sans-serif !important;
}
h1,
h2,
h3,
h4,
h5,
p {
  color: $preto;
}
p {
  color: rgba(0, 0, 0, 0.86);
}
body {
  background-color: $cinza-l90 !important;
  -webkit-font-smoothing: antialiased;
}
.primary-text {
  opacity: 0.87 !important;
}
.secondary-text {
  opacity: 0.54 !important;
}
.mf-h1 {
  font-family: 'Lato', sans-serif;
  font-weight: lighter;
  font-size: 96px;
  letter-spacing: -2px;
}
.mf-h2 {
  font-family: 'Lato', sans-serif;
  font-weight: lighter;
  font-size: 72px;
  letter-spacing: -1px;
}
.mf-h3 {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 56px;
  letter-spacing: 0;
}
.mf-h4 {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 40px;
  letter-spacing: 0.4px;
}
.mf-h5 {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 24px;
  letter-spacing: 0.4px;
}
.mf-h6 {
  font-family: 'Lato', sans-serif;
  font-weight: bold;
  font-size: 18px;
  letter-spacing: 0.4px;
}
.mf-subtitle-1 {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 16px;
  letter-spacing: 0.2px;
}
.mf-subtitle-2 {
  font-family: 'Lato', sans-serif;
  font-weight: bold;
  font-size: 14px;
  letter-spacing: 0.2px;
}
.mf-body-1 {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 16px;
  letter-spacing: 0.4px;
}
.mf-body-2 {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 14px;
  letter-spacing: 0.4px;
}
.mf-button {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 14px;
  letter-spacing: 0.4px;
}
.mf-caption {
  font-family: 'Lato', sans-serif;
  font-weight: normal;
  font-size: 12px;
  letter-spacing: 0.2px;
}
.mf-overline {
  font-family: 'Lato', sans-serif;
  font-weight: bold;
  font-size: 10px;
  letter-spacing: 1px;
}

.unset-capitalize {
  text-transform: unset !important;
  letter-spacing: 0.02px !important;
}

.user-select-none {
  user-select: none;
}
.v-application,
.base-page-layout {
  font-family: 'Lato', sans-serif;
  background-color: $cinza-l90 !important;
}
.fade-enter-active {
  transition: opacity 0.5s;
}

.fade-leave-active {
  opacity: 0;
}

.fade-enter {
  opacity: 0;
}

.fade-in-up-enter-active {
  transition: all 0.5s;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-in-up-leave-active {
  opacity: 0;
}

.fade-in-up-enter {
  opacity: 0;
  transform: translate3d(0, 100%, 0);
}
.overflow-x {
  overflow-x: auto;
}
.responsive-image {
  width: 100%;
  height: auto;
}
.ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.bold {
  font-weight: bold !important;
}
.child-view {
  position: absolute;
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}
.slide-up-enter {
  opacity: 0;
  -webkit-transform: translate(0, 30px);
  transform: translate(0, 30px);
}
.slide-up-leave-active {
  opacity: 0;
  -webkit-transform: translate(0, -30px);
  transform: translate(0, -30px);
}
td a {
  text-decoration: underline;
}
@media print {
  .v-toolbar {
    display: none;
  }
  .v-navigation-drawer {
    display: none;
  }
  .v-content {
    padding: 0 !important;
  }
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-text {
  cursor: text !important;
}

.v-snack {
  z-index: 10000 !important;
}
.v-chip:not(.v-chip--active) {
  background: $cinza-l80 !important;
  color: $cinza-0 !important;
}

.v-card {
  box-shadow: $shadow-card !important;
}

.mf-card {
  display: flex;
  background-color: $branco;
  border-radius: 8px;
  box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.16);
}
</style>
